import {
  type AddCardDisputeEvidenceRequest,
  type CardDispute,
  CardDisputeAccountStatus,
  CardDisputeCurrencyError,
  type CardDisputeDocumentEvidenceInput,
  type CardDisputeEligibilityRequest,
  type CardDisputeFraudReasonDetails,
  CardDisputeMerchandiseOrServices,
  CardDisputeMerchantContactMethod,
  CardDisputeOtherPaymentProof,
  type CardDisputeReasonDetails,
  CardDisputeReservationCertification,
  CardDisputeReturnMethod,
  CardDisputeReturnRefusalReason,
  CardDisputeServiceType,
  CardDisputeTransactionCodeError,
  CardDisputeWorkflow,
  type CardTransaction,
  type CreateCardDisputeRequest,
} from '@column/api';
import { Currencies } from '@column/column-ui-kit';

import { type CardDisputeReason, toApiReason } from '~/lib/disputes/labels';

import {
  ACCOUNT_STATUS_LABELS,
  CANCELED_RECURRING_ENDED_BY_LABELS,
  CURRENCY_ERROR_LABELS,
  FRAUD_TYPE_LABELS,
  MERCHANDISE_OR_SERVICES_LABELS,
  MERCHANT_CONTACT_METHOD_LABELS,
  MERCHANT_RETURN_REFUSAL_REASON_LABELS,
  ORIGINAL_CREDIT_NOT_ACCEPTED_REASON_LABELS,
  OTHER_PAYMENT_PROOF_LABELS,
  RESERVATION_CERTIFICATION_LABELS,
  RETURN_METHOD_LABELS,
  SERVICE_TYPE_LABELS,
  TRANSACTION_CODE_ERROR_LABELS,
} from './questionnaireLabels';

/**
 * Reason-questionnaire state, serialization, and readiness for dispute initiation and draft
 * repair. Pure logic: no React and no API calls, so the new-dispute wizard and the draft-repair
 * panel (and Part 05's repair flows) share one set of rules.
 *
 * `reasonQuestions` is the one description of each reason's questionnaire: which questions the
 * current answers ask, which of them are required, and which answers Visa hard-stops. The form
 * renders it, readiness reads it, and serialization sends only the answers it asks for, so a stale
 * answer to a question no longer asked never reaches the API.
 *
 * The backend mirrors live in `pkg/cards/models/card_dispute_reason_details*.go` and
 * `card_dispute_processing_error_reason_details.go` (`MissingRequiredDetail`, whose field order the
 * questions follow), `pkg/cards/models/card_dispute.go` (narrative and document requirements), and
 * `pkg/cards/disputes/api*.go` (write-time hard stops and eligibility). Keep the two in sync.
 */

type FraudType = NonNullable<CardDisputeFraudReasonDetails['fraud_type']>;
type MerchantReturnRefusalReason = `${CardDisputeReturnRefusalReason}`;

export type { FraudType, MerchantReturnRefusalReason };

/** Whether a dispute reason requires a cardholder narrative at submission. Mirrors `RequiresCardholderNarrativeEvidence`. */
export const REASON_REQUIRES_NARRATIVE: Record<CardDisputeReason, boolean> = {
  fraud: false,
  no_authorization: false,
  incorrect_transaction_code: false,
  incorrect_currency: false,
  incorrect_account_number: false,
  incorrect_amount: false,
  duplicate_processing: false,
  paid_by_other_means: false,
  invalid_data: false,
  merchandise_services_not_received: true,
  canceled_recurring_transaction: true,
  not_as_described: true,
  damaged_or_defective_merchandise: true,
  quality_of_merchandise_or_services: true,
  terms_of_contract: true,
  counterfeit_merchandise: true,
  misrepresentation: true,
  credit_not_processed: true,
  canceled_merchandise_or_services: true,
  original_credit_not_accepted: true,
  non_receipt_cash_at_atm: true,
  other: true,
};

/**
 * Whether a dispute reason always requires at least one uploaded document at submission. Mirrors
 * `RequiresEvidenceDocumentEvidence`; answers can pull a document in for other reasons, see
 * `requiresEvidenceDocument`.
 */
export const REASON_REQUIRES_DOCUMENT: Record<CardDisputeReason, boolean> = {
  fraud: false,
  no_authorization: false,
  incorrect_transaction_code: false,
  incorrect_currency: false,
  incorrect_account_number: false,
  incorrect_amount: true,
  duplicate_processing: false,
  paid_by_other_means: false,
  invalid_data: false,
  merchandise_services_not_received: false,
  canceled_recurring_transaction: false,
  not_as_described: false,
  damaged_or_defective_merchandise: false,
  quality_of_merchandise_or_services: false,
  terms_of_contract: false,
  counterfeit_merchandise: true,
  misrepresentation: false,
  credit_not_processed: true,
  canceled_merchandise_or_services: false,
  original_credit_not_accepted: false,
  non_receipt_cash_at_atm: false,
  other: false,
};

/** Fraud-related reasons follow the allocation workflow, everything else collaboration. Mirrors `DisputeWorkflow`. */
export const REASON_WORKFLOW: Record<CardDisputeReason, CardDisputeWorkflow> = {
  fraud: CardDisputeWorkflow.ALLOCATION,
  no_authorization: CardDisputeWorkflow.ALLOCATION,
  incorrect_transaction_code: CardDisputeWorkflow.COLLABORATION,
  incorrect_currency: CardDisputeWorkflow.COLLABORATION,
  incorrect_account_number: CardDisputeWorkflow.COLLABORATION,
  incorrect_amount: CardDisputeWorkflow.COLLABORATION,
  duplicate_processing: CardDisputeWorkflow.COLLABORATION,
  paid_by_other_means: CardDisputeWorkflow.COLLABORATION,
  invalid_data: CardDisputeWorkflow.COLLABORATION,
  merchandise_services_not_received: CardDisputeWorkflow.COLLABORATION,
  canceled_recurring_transaction: CardDisputeWorkflow.COLLABORATION,
  not_as_described: CardDisputeWorkflow.COLLABORATION,
  damaged_or_defective_merchandise: CardDisputeWorkflow.COLLABORATION,
  quality_of_merchandise_or_services: CardDisputeWorkflow.COLLABORATION,
  terms_of_contract: CardDisputeWorkflow.COLLABORATION,
  counterfeit_merchandise: CardDisputeWorkflow.COLLABORATION,
  misrepresentation: CardDisputeWorkflow.COLLABORATION,
  credit_not_processed: CardDisputeWorkflow.COLLABORATION,
  canceled_merchandise_or_services: CardDisputeWorkflow.COLLABORATION,
  original_credit_not_accepted: CardDisputeWorkflow.COLLABORATION,
  non_receipt_cash_at_atm: CardDisputeWorkflow.COLLABORATION,
  other: CardDisputeWorkflow.COLLABORATION,
};

/** Reasons whose questionnaire collects no reason-specific details. */
export const REASON_WITHOUT_DETAILS: CardDisputeReason[] = ['non_receipt_cash_at_atm', 'other'];

/** Whether a partial claim must explain why. `incorrect_amount` is partial by nature. Mirrors `RequiresPartialDisputeExplanation`. */
export const reasonRequiresPartialExplanation = (reason: CardDisputeReason): boolean => reason !== 'incorrect_amount';

/** Whether the reason can issue provisional credit. An original-credit dispute returns funds the cardholder received. */
export const reasonSupportsProvisionalCredit = (reason: CardDisputeReason): boolean =>
  reason !== 'original_credit_not_accepted';

/**
 * Whether the reason only accepts a claim for less than the transaction amount: the IES hard-stops a whole-amount
 * `incorrect_amount` claim (DisputeAmount notes) and `credit_instead_of_reversal` claim (TransactionCodeIncorrect notes).
 * Mirrors `claimsWholeAmountOfPartialOnlyReason`.
 */
export const reasonRequiresPartialClaim = (reason: CardDisputeReason): boolean =>
  reason === 'incorrect_amount' || reason === 'incorrect_transaction_code';

// Visa limits on canceled-recurring merchant contact details, from ROLSIRT.xsd via the backend's TextFields.
export const MERCHANT_CONTACT_DETAIL_LIMITS = {
  merchant_contact_phone_number: 20,
  merchant_contact_email_address: 64,
  merchant_contact_physical_address: 50,
  merchant_contact_application_name: 200,
} as const;

export type MerchantContactDetailField = keyof typeof MERCHANT_CONTACT_DETAIL_LIMITS;

// Backend length limits count Unicode characters of the trimmed text, as the XSD's maxLength does, never bytes.
export const MAX_REASON_FOR_PARTIAL_DISPUTE_LENGTH = 10_000;
/** vsi:NoteType: cardholder, evidence, respond and close narratives. Mirrors `MaxCardDisputeNarrativeLength`. */
export const MAX_NARRATIVE_LENGTH = 5_000;
/** vsi:Desc10000Type, the limit on nearly every free-text questionnaire answer. */
const MAX_DESCRIPTION_LENGTH = 10_000;
/** vsi:Desc5000Type, DispositionOfMerchandise; counterfeit's disposition is a 10000-character element instead. */
const MAX_MERCHANDISE_DISPOSITION_LENGTH = 5_000;
const MAX_RETURN_TRACKING_NUMBER_LENGTH = 50;
const MAX_CARD_MAILED_POSTAL_CODE_LENGTH = 9;
// VROL asks the late-claim questions after 120 days; the API requires them 7 days early, since a dispute files
// asynchronously after it is accepted (`thresholdDays`).
const LATE_CLAIM_DAYS = 113;
const MERCHANT_ACCEPT_CANCELLATION_DAYS = 15;
const CREDIT_ISSUED_LATE_DAYS = 113;
const TIMESHARE_CANCELLATION_DAYS = 14;

const RESTAURANT_MERCHANT_CATEGORY_CODES = ['5812', '5813', '5814'];
const ATM_MERCHANT_CATEGORY_CODE = '6011';
// Visa T&E MCCs: airlines, car rental, lodging and cruise ships, passenger railways.
const TRAVEL_AND_ENTERTAINMENT_MCC_RANGES: [number, number][] = [
  [3000, 3299],
  [3300, 3500],
  [3501, 3999],
  [4112, 4112],
  [4511, 4511],
  [7011, 7011],
  [7512, 7512],
];

const CARD_TRANSACTION_ID_PATTERN = /^ctxn_[0-9A-Za-z]{27}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

const characterLength = (value: string): number => [...value].length;

const trimmed = (value: string | undefined): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const next = value.trim();
  return next === '' ? undefined : next;
};

/** Whether trimmed text exceeds a backend limit, counting characters. */
export const exceedsCharacterLimit = (value: string | undefined, limit: number): boolean => {
  const next = trimmed(value);
  return next !== undefined && characterLength(next) > limit;
};

/** Whether a narrative is longer than Visa's Note accepts. Mirrors `validateCardDisputeNarrativeLength`. */
export const narrativeTooLong = (value: string | undefined): boolean =>
  exceedsCharacterLimit(value, MAX_NARRATIVE_LENGTH);

export const NARRATIVE_TOO_LONG_MESSAGE = `The statement is limited to ${MAX_NARRATIVE_LENGTH.toLocaleString('en-US')} characters.`;

/** Fractional digits for a currency from the canonical currency metadata. `undefined` for codes the repository does not know: callers must fail safe rather than assume two decimals. */
export const currencyDecimalDigits = (currencyCode: string): number | undefined =>
  Currencies.find((currency) => currency.code === currencyCode)?.decimalDigits;

/**
 * Currency-aware major-unit (decimal string) → smallest-unit (integer) conversion using string
 * math, so money values never pass through a float. Returns undefined for input the currency
 * cannot represent (too many fractional digits), non-numeric input, or an unknown currency —
 * never a silently mis-scaled amount.
 */
export const parseMajorUnitsToSmallestUnit = (value: string | undefined, currencyCode: string): number | undefined => {
  const cleaned = (value ?? '').trim().replace(/,/g, '');
  if (cleaned === '' || !/^\d+(\.\d+)?$/.test(cleaned)) {
    return undefined;
  }

  const decimalDigits = currencyDecimalDigits(currencyCode);
  if (decimalDigits === undefined) {
    return undefined;
  }
  const [wholePart, fractionalPart = ''] = cleaned.split('.');
  if (fractionalPart.length > decimalDigits) {
    return undefined;
  }

  const digits = `${wholePart === '0' ? '' : wholePart}${fractionalPart.padEnd(decimalDigits, '0')}`;
  const parsed = Number(digits);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

/**
 * Smallest-unit (integer) → major-unit decimal string for form inputs, using string math so no
 * floating-point value is ever constructed from money.
 */
export const formatSmallestUnitToMajorUnits = (amount: number | undefined, currencyCode: string): string => {
  if (amount === undefined || !Number.isSafeInteger(amount) || amount <= 0) {
    return '';
  }
  const decimalDigits = currencyDecimalDigits(currencyCode);
  if (decimalDigits === undefined) {
    return '';
  }
  const digits = amount.toString().padStart(decimalDigits + 1, '0');
  const whole = digits.slice(0, digits.length - decimalDigits);
  const fraction = decimalDigits > 0 ? `.${digits.slice(digits.length - decimalDigits)}` : '';
  return `${whole}${fraction}`;
};

const hasDate = (value: string | undefined): boolean => DATE_PATTERN.test((value ?? '').trim());

const dayNumber = (date: string): number => {
  const [year, month, day] = date.trim().split('-').map(Number);
  return Date.UTC(year, month - 1, day) / DAY_MS;
};

/** Calendar days `date` falls before `reference`, negative when after. Both are `yyyy-mm-dd`. */
const daysBefore = (date: string, reference: string): number => dayNumber(reference) - dayNumber(date);

/** The UTC calendar date of an instant, `yyyy-mm-dd`, or undefined for an invalid date. */
export const utcCalendarDate = (date: Date): string | undefined =>
  Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);

/** What conditional requirements read about the disputed card transaction. */
export interface DisputeTransactionFacts {
  id: string;
  /** The card acceptor's merchant category code, `''` when the network sent none. */
  merchantCategoryCode: string;
  /** The UTC calendar date the transaction was processed, approximated like the backend by when Column recorded it. */
  processingDate?: string;
  /** The cardholder settled total's magnitude, in smallest units: what eligibility measures a new claim against. */
  settledAmount?: number;
  /**
   * What the cardholder was charged, in smallest units, excluding dispute credits and debits so a provisional credit
   * does not make a partial claim look whole. Mirrors `DisputableAmount`.
   */
  disputableAmount?: number;
  /** The currency the merchant processed the transaction in, as opposed to the billing currency. Mirrors `TransactionCurrency`. */
  transactionCurrency?: string;
  /** Whether the transaction settled as a credit to the cardholder rather than a charge. */
  settledAsCredit: boolean;
  isOriginalCredit: boolean;
  isATMCashWithdrawal: boolean;
}

/**
 * Beyond the answers, requirements read the clock and the disputed transaction (MCC, processing
 * date, amount). `transaction` is undefined while the dashboard has not loaded it; like the backend
 * with no transaction, requirements that depend on it are not demanded then, and their questions are
 * offered as optional with an explanation.
 */
export interface DisputeRequirementContext {
  /** Today's UTC calendar date, `yyyy-mm-dd`. */
  today: string;
  transaction?: DisputeTransactionFacts;
}

// Event types, as the API serializes them, that move money for a dispute rather than change the charge.
const DISPUTE_MOVEMENT_EVENT_TYPES = [
  'dispute_credit',
  'dispute_credit_reversal',
  'dispute_debit',
  'dispute_debit_reversal',
];
// Event types that rule out an ATM cash withdrawal: a balance inquiry, or money moving toward the cardholder.
const NON_WITHDRAWAL_EVENT_TYPES = [
  'balance_inquiry',
  'refund_authorization',
  'refund_financial',
  'original_credit',
  'credit_clear',
];

/**
 * The requirement facts of a card transaction. The public transaction leaves `type` unset, so what the transaction is
 * comes from its events (the dashboard's GET loads every event), as the backend derives it.
 */
export const disputeTransactionFacts = (transaction: CardTransaction): DisputeTransactionFacts => {
  const settled = transaction.total_amounts?.cardholder_settled?.amount;
  const events = transaction.events ?? [];
  // Event types outside the generated union (e.g. dispute_credit) still arrive as strings.
  const eventTypes = events.map((event) => event.event_type as string);
  const merchantCategoryCode = (transaction.card_acceptor_details?.merchant_category_code ?? '').trim();
  const chargedAmount = events
    .filter((event) => !DISPUTE_MOVEMENT_EVENT_TYPES.includes(event.event_type as string))
    .reduce((total, event) => total + (event.amounts?.cardholder_settled?.amount ?? 0), 0);
  const disputable = chargedAmount !== 0 ? chargedAmount : settled;
  const transactionCurrency =
    trimmed(transaction.total_amounts?.merchant_settled?.currency_code ?? undefined) ??
    trimmed(transaction.total_amounts?.merchant_authorized?.currency_code ?? undefined);
  return {
    id: transaction.id,
    merchantCategoryCode,
    processingDate: utcCalendarDate(new Date(transaction.created_at)),
    settledAmount: settled === undefined ? undefined : Math.abs(settled),
    disputableAmount: disputable === undefined ? undefined : Math.abs(disputable),
    transactionCurrency,
    settledAsCredit: settled !== undefined && settled > 0,
    isOriginalCredit:
      Boolean(transaction.transaction_details?.original_credit_data) || eventTypes.includes('original_credit'),
    isATMCashWithdrawal:
      merchantCategoryCode === ATM_MERCHANT_CATEGORY_CODE &&
      !eventTypes.some((eventType) => NON_WITHDRAWAL_EVENT_TYPES.includes(eventType)),
  };
};

export const disputeRequirementContext = (
  transaction?: CardTransaction,
  now: Date = new Date()
): DisputeRequirementContext => ({
  today: utcCalendarDate(now) ?? new Date().toISOString().slice(0, 10),
  transaction: transaction ? disputeTransactionFacts(transaction) : undefined,
});

const isTravelAndEntertainment = (merchantCategoryCode: string): boolean => {
  if (!/^\d{4}$/.test(merchantCategoryCode)) {
    return false;
  }
  const code = Number(merchantCategoryCode);
  return TRAVEL_AND_ENTERTAINMENT_MCC_RANGES.some(([low, high]) => code >= low && code <= high);
};

/**
 * Why Visa rejects the reason for this transaction, or undefined when it applies or the transaction
 * is unknown. Mirrors the `transaction_not_settled` and `reason_not_applicable_to_transaction`
 * eligibility rules; an unknown MCC blocks neither ATM rule.
 */
export const reasonNotApplicableMessage = (
  reason: CardDisputeReason,
  transaction: DisputeTransactionFacts | undefined
): string | undefined => {
  if (!transaction) {
    return undefined;
  }
  if (reason === 'original_credit_not_accepted') {
    return transaction.isOriginalCredit
      ? undefined
      : 'Original credit not accepted only applies to original credits pushed to the card.';
  }
  if (transaction.settledAsCredit) {
    return 'This transaction credited the cardholder. Only an original credit can be disputed, as original credit not accepted.';
  }
  if (transaction.merchantCategoryCode === '') {
    return undefined;
  }
  if (reason === 'non_receipt_cash_at_atm' && !transaction.isATMCashWithdrawal) {
    return 'Non-receipt of cash at ATM only applies to ATM cash withdrawals.';
  }
  if (reason === 'merchandise_services_not_received' && transaction.isATMCashWithdrawal) {
    return 'Merchandise or services not received does not apply to ATM cash withdrawals. Use non-receipt of cash at ATM.';
  }
  return undefined;
};

type FormAnswer<V> = V extends boolean ? boolean : V extends readonly (infer U)[] ? U[] : string;

/** Form answers for a reason sub-object: booleans stay booleans, arrays stay arrays, and every other value is the input string. */
export type ReasonAnswers<T> = { [K in keyof T]?: FormAnswer<NonNullable<T[K]>> };

export type CanceledRecurringEndedBy = keyof typeof CANCELED_RECURRING_ENDED_BY_LABELS;

/**
 * Questionnaire form state, one answer map per reason. `false` is a valid supplied answer for every
 * boolean and is never conflated with unanswered (`undefined`); factual statements are never
 * prefilled affirmatively. Select answers are kept as strings and validated against the options.
 * `canceled_recurring_transaction.ended_by` is dashboard-only and never sent.
 */
export type DisputeReasonFormState = {
  [K in keyof CardDisputeReasonDetails]?: ReasonAnswers<NonNullable<CardDisputeReasonDetails[K]>> &
    (K extends 'canceled_recurring_transaction' ? { ended_by?: string } : unknown);
};

export const emptyReasonFormState = (): DisputeReasonFormState => ({
  canceled_recurring_transaction: { merchant_contact_methods: [] },
});

/** Machine-readable identifiers for missing required questionnaire answers: the API field inside the reason sub-object. */
export type ReasonFieldKey = string;

export type ReasonQuestionKind = 'select' | 'text' | 'multiline' | 'date' | 'boolean' | 'amount' | 'contact_methods';

export interface ReasonQuestionOption {
  value: string;
  label: string;
}

export interface ReasonQuestion {
  /** The answer's key in the reason's form state and API sub-object. */
  field: ReasonFieldKey;
  kind: ReasonQuestionKind;
  label: string;
  description?: string;
  required: boolean;
  options?: ReasonQuestionOption[];
  placeholder?: string;
  /** The most characters the backend accepts for the trimmed answer, from the element's XSD maxLength. */
  maxLength?: number;
  testId?: string;
  /** Why the current answer is one the API rejects or Visa hard-stops. Shown inline; blocks saving. */
  hardStop?: string;
  /** False for dashboard-only questions that select a branch but are never sent. */
  serialize?: boolean;
  /** The effective answer when it is derived from other answers rather than stored under `field`. */
  derivedValue?: string;
}

type ReasonAnswer = string | boolean | string[] | undefined;
type ReasonAnswerMap = Partial<Record<string, ReasonAnswer>>;

type QuestionOverrides = Partial<Omit<ReasonQuestion, 'field' | 'kind' | 'label'>>;

const ask =
  (kind: ReasonQuestionKind) =>
  (field: string, label: string, overrides: QuestionOverrides = {}): ReasonQuestion => ({
    field,
    kind,
    label,
    required: true,
    ...overrides,
  });

// Free-text answers default to Visa's 10000-character description limit; formatted answers opt out.
const askText = (field: string, label: string, overrides: QuestionOverrides = {}): ReasonQuestion =>
  ask('text')(field, label, { maxLength: MAX_DESCRIPTION_LENGTH, ...overrides });
const askLongText = (field: string, label: string, overrides: QuestionOverrides = {}): ReasonQuestion =>
  ask('multiline')(field, label, { maxLength: MAX_DESCRIPTION_LENGTH, ...overrides });
const askDate = ask('date');
const askYesNo = ask('boolean');
const askAmount = ask('amount');
const askSelect = (
  field: string,
  label: string,
  options: ReasonQuestionOption[],
  overrides: QuestionOverrides = {}
): ReasonQuestion => ({ field, kind: 'select', label, options, required: true, ...overrides });

const optionsFrom = <V extends string>(labels: Record<V, string>, values?: readonly V[]): ReasonQuestionOption[] =>
  (values ?? (Object.keys(labels) as V[])).map((value) => ({ value, label: labels[value] }));

const answersOf = (state: DisputeReasonFormState, reason: CardDisputeReason): ReasonAnswerMap =>
  ((state as Partial<Record<string, ReasonAnswerMap>>)[reason] ?? {}) as ReasonAnswerMap;

const stringAnswer = (answers: ReasonAnswerMap, field: string): string | undefined => {
  const value = answers[field];
  return typeof value === 'string' ? value : undefined;
};

const OPTIONAL_NO = 'Optional. Treated as No when unanswered.';
const TRANSACTION_UNKNOWN = 'The transaction has not loaded, so answer it if it applies.';

const RESOLUTION_HARD_STOP =
  'Visa does not accept this dispute: the cardholder must attempt to resolve it with the merchant unless local law prohibits it.';

/**
 * Flags a free-text answer longer than the backend accepts. Mirrors `validateCardDisputeReasonDetailsTextLengths` for the
 * asked questions; a question's own hard stop, such as a format error, takes precedence.
 */
const withLengthHardStops = (questions: ReasonQuestion[], answers: ReasonAnswerMap): ReasonQuestion[] =>
  questions.map((question) =>
    question.hardStop === undefined &&
    question.maxLength !== undefined &&
    exceedsCharacterLimit(stringAnswer(answers, question.field), question.maxLength)
      ? { ...question, hardStop: `This answer is limited to ${question.maxLength.toLocaleString('en-US')} characters.` }
      : question
  );

/** The related-transaction ID format, and the backend's different-transaction check. Card and account matching stays server-side. */
const cardTransactionIdHardStop = (value: string | undefined, ctx: DisputeRequirementContext): string | undefined => {
  const next = trimmed(value);
  if (next === undefined) {
    return undefined;
  }
  if (!CARD_TRANSACTION_ID_PATTERN.test(next)) {
    return 'Enter a card transaction ID: ctxn_ followed by 27 letters and digits.';
  }
  if (next === ctx.transaction?.id) {
    return 'Enter the other card transaction, not the disputed one.';
  }
  return undefined;
};

const MERCHANDISE_OR_SERVICES_OPTIONS = optionsFrom(MERCHANDISE_OR_SERVICES_LABELS);
const RETURN_METHOD_OPTIONS = optionsFrom(RETURN_METHOD_LABELS);
const ALL_REFUSAL_REASONS = optionsFrom(MERCHANT_RETURN_REFUSAL_REASON_LABELS);
// Misrepresentation and canceled merchandise accept only the certifications where the merchant itself refused or advised against the return.
const MERCHANT_DIRECTED_REFUSAL_REASONS = optionsFrom(MERCHANT_RETURN_REFUSAL_REASON_LABELS, [
  CardDisputeReturnRefusalReason.REFUSED_RETURN_AUTHORIZATION,
  CardDisputeReturnRefusalReason.REFUSED_ACCEPT_RETURNED_MERCHANDISE,
  CardDisputeReturnRefusalReason.ADVISED_NOT_TO_RETURN,
]);
const CARRIER_RETURN_METHODS: string[] = [
  CardDisputeReturnMethod.FEDEX,
  CardDisputeReturnMethod.DHL,
  CardDisputeReturnMethod.UPS,
  CardDisputeReturnMethod.POSTAL_SERVICE,
];
const CARRIER_OR_OTHER_RETURN_METHODS = [...CARRIER_RETURN_METHODS, CardDisputeReturnMethod.OTHER];

/**
 * Why an issuer cannot file a fraud dispute with a stored fraud type. They stay displayable so disputes stored before
 * they were retired still load. Mirrors `IsIssuerFileable` and `unfileableFraudTypeDetail`.
 */
const RETIRED_FRAUD_TYPE_HARD_STOPS: Partial<Record<FraudType, string>> = {
  acquirer_reported:
    'Fraud reported by the acquirer can no longer be filed: Visa reserves it for acquirers. Choose the fraud type the cardholder reported.',
  merchant_misrepresentation:
    'Merchant misrepresentation can no longer be filed as fraud: a fraud dispute certifies the cardholder denies authorizing or participating in the transaction. File a scam the cardholder took part in under a consumer dispute reason, such as misrepresentation or merchandise or services not received.',
  manipulation_of_account_holder:
    'Manipulation of the account holder can no longer be filed as fraud: a fraud dispute certifies the cardholder denies authorizing or participating in the transaction. File a scam the cardholder took part in under a consumer dispute reason, such as misrepresentation or merchandise or services not received.',
};
const ISSUER_FILEABLE_FRAUD_TYPES = (Object.keys(FRAUD_TYPE_LABELS) as FraudType[]).filter(
  (fraudType) => RETIRED_FRAUD_TYPE_HARD_STOPS[fraudType] === undefined
);
const PIN_PREFERRING_FRAUD_TYPES: string[] = ['lost', 'stolen', 'card_not_received_as_issued'];

const DOCUMENT_PAYMENT_PROOFS: string[] = [
  CardDisputeOtherPaymentProof.CASH_RECEIPT,
  CardDisputeOtherPaymentProof.CANCELLED_CHECK,
  CardDisputeOtherPaymentProof.OTHER_CARD_STATEMENT,
  CardDisputeOtherPaymentProof.OTHER,
];

const CURRENCY_OPTIONS: ReasonQuestionOption[] = [...Currencies]
  .sort((a, b) => a.code.localeCompare(b.code))
  .map((currency) => ({ value: currency.code, label: `${currency.code} · ${currency.name}` }));

const CONTACT_METHOD_VALUES = Object.values(CardDisputeMerchantContactMethod) as string[];

/** The attempt-to-resolve question, and whether local law prohibits the attempt once it was not made. Mirrors `missingResolutionDetail`. */
const resolutionQuestions = (answers: ReasonAnswerMap): ReasonQuestion[] => {
  const questions = [
    askYesNo('cardholder_attempted_resolution', 'Did the cardholder attempt to resolve the dispute with the merchant?'),
  ];
  if (answers.cardholder_attempted_resolution === false) {
    questions.push(
      askYesNo(
        'resolution_prohibited_by_local_law',
        'Is an attempt to resolve the dispute with the merchant prohibited by local law or regulations?',
        { hardStop: answers.resolution_prohibited_by_local_law === false ? RESOLUTION_HARD_STOP : undefined }
      )
    );
  }
  return questions;
};

/** The late-claim questions. Mirrors `CardDisputePreviousNegotiation.missingDetail`. */
const negotiationQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const receivedDate = stringAnswer(answers, 'received_date');
  if (!hasDate(receivedDate) || daysBefore(receivedDate!, ctx.today) <= LATE_CLAIM_DAYS) {
    return [];
  }
  const processingDate = ctx.transaction?.processingDate;
  if (ctx.transaction && (processingDate === undefined || daysBefore(processingDate, ctx.today) <= LATE_CLAIM_DAYS)) {
    return [];
  }
  const required = ctx.transaction !== undefined;
  const questions = [
    askYesNo(
      'previous_negotiation_evidence',
      'Is there evidence of previous ongoing negotiations between the cardholder and the merchant?',
      {
        required,
        description: required
          ? 'Visa asks this once the purchase and the transaction are more than 120 days old.'
          : `Required once the transaction is more than 113 days old. ${TRANSACTION_UNKNOWN}`,
      }
    ),
  ];
  if (answers.previous_negotiation_evidence === true) {
    questions.push(
      askDate('negotiation_start_date', 'When did the negotiations begin?', { required }),
      askLongText('negotiation_details', 'What were the negotiations between the cardholder and the merchant?', {
        required,
      }),
      askDate('cardholder_first_notified_issuer_date', 'When did the cardholder first notify you of the dispute?', {
        required,
      })
    );
  }
  return questions;
};

interface ReturnTreeOptions {
  /** The return methods for which Visa receives the tracking number. */
  trackingNumberMethods: string[];
  refusalReasons: ReasonQuestionOption[];
  /** Misrepresentation asks neither the disposition nor, as required, the attempt description. */
  misrepresentation?: boolean;
}

/** The merchandise return tree. Mirrors `missingReturnedDetail` and `missingDescribedReturnDetail`. */
const returnTreeQuestions = (answers: ReasonAnswerMap, options: ReturnTreeOptions): ReasonQuestion[] => {
  const questions = [askYesNo('cardholder_returned_merchandise', 'Did the cardholder return the merchandise?')];
  if (answers.cardholder_returned_merchandise === true) {
    const returnMethod = stringAnswer(answers, 'return_method');
    questions.push(
      askDate('returned_date', 'When did the cardholder return the merchandise?'),
      askDate('merchant_received_return_date', 'When did the merchant receive the returned merchandise?'),
      askSelect('return_method', 'How was the merchandise returned?', RETURN_METHOD_OPTIONS)
    );
    if (returnMethod === CardDisputeReturnMethod.OTHER) {
      questions.push(askText('return_method_details', 'Describe how the merchandise was returned'));
    }
    if (returnMethod !== undefined && options.trackingNumberMethods.includes(returnMethod)) {
      questions.push(
        askText('return_tracking_number', 'Shipment tracking number', {
          required: false,
          description: 'Optional.',
          maxLength: MAX_RETURN_TRACKING_NUMBER_LENGTH,
        })
      );
    }
    return questions;
  }
  if (answers.cardholder_returned_merchandise !== false) {
    return questions;
  }
  questions.push(askYesNo('cardholder_attempted_return', 'Did the cardholder attempt to return the merchandise?'));
  if (answers.cardholder_attempted_return !== true) {
    return questions;
  }
  if (options.misrepresentation) {
    questions.push(
      askDate('attempted_return_date', 'When did the cardholder attempt to return the merchandise?'),
      askLongText('return_attempt_details', 'How did the cardholder attempt to return the merchandise?', {
        required: false,
        description: 'Optional.',
      })
    );
  } else {
    questions.push(
      askLongText('merchandise_disposition', 'What is the current disposition of the merchandise?', {
        placeholder: 'For example, retained by the cardholder',
        maxLength: MAX_MERCHANDISE_DISPOSITION_LENGTH,
      }),
      askDate('attempted_return_date', 'When did the cardholder attempt to return the merchandise?'),
      askLongText('return_attempt_details', 'How did the cardholder attempt to return the merchandise?')
    );
  }
  questions.push(
    askSelect(
      'merchant_return_refusal_reason',
      'Why did the merchant not provide or accept a return?',
      options.refusalReasons
    )
  );
  return questions;
};

/** A canceled service, its date and method, and the merchant's acceptance while the cancellation is recent. */
const servicesCancellationQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const questions = [askYesNo('cardholder_canceled', 'Did the cardholder cancel the services?')];
  if (answers.cardholder_canceled !== true) {
    return questions;
  }
  questions.push(
    askDate('cancellation_date', 'When did the cardholder cancel the services?'),
    askLongText('cancellation_method', 'How and why did the cardholder cancel the services?')
  );
  questions.push(...merchantAcceptedCancellationQuestions(answers, ctx));
  return questions;
};

/** Asked while the cancellation is at most 15 days old. Mirrors `IsRecentCardDisputeCancellation`. */
const merchantAcceptedCancellationQuestions = (
  answers: ReasonAnswerMap,
  ctx: DisputeRequirementContext
): ReasonQuestion[] => {
  const cancellationDate = stringAnswer(answers, 'cancellation_date');
  if (!hasDate(cancellationDate) || daysBefore(cancellationDate!, ctx.today) > MERCHANT_ACCEPT_CANCELLATION_DAYS) {
    return [];
  }
  return [
    askYesNo('merchant_accepted_cancellation', 'Did the merchant accept the cancellation?', {
      description: 'Visa asks this while the cancellation is no more than 15 days old.',
    }),
  ];
};

/** Required for eating places. Mirrors `IsRestaurantTransaction`. */
const foodQualityQuestions = (ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const label = 'Is the dispute about the quality of food from an eating place or restaurant?';
  if (!ctx.transaction) {
    return [
      askYesNo('food_quality_issue', label, {
        required: false,
        description: `Required for restaurants, bars and fast food merchants (MCC 5812, 5813 or 5814). ${TRANSACTION_UNKNOWN}`,
      }),
    ];
  }
  return RESTAURANT_MERCHANT_CATEGORY_CODES.includes(ctx.transaction.merchantCategoryCode)
    ? [askYesNo('food_quality_issue', label)]
    : [];
};

const merchandiseOrServicesQuestion = (label = 'What was purchased?'): ReasonQuestion =>
  askSelect('merchandise_or_services', label, MERCHANDISE_OR_SERVICES_OPTIONS);

const nonFiatNotAsDescribedQuestion = (): ReasonQuestion =>
  askYesNo(
    'non_fiat_currency_not_as_described',
    'Was the purchase of non-fiat currency or NFTs, and did what the cardholder received not match its description at the time of the transaction?'
  );

/** Mirrors `CardDisputeFraudReasonDetails.MissingRequiredDetail`. */
const fraudQuestions = (answers: ReasonAnswerMap): ReasonQuestion[] => {
  const fraudType = stringAnswer(answers, 'fraud_type');
  const retiredHardStop = fraudType === undefined ? undefined : RETIRED_FRAUD_TYPE_HARD_STOPS[fraudType as FraudType];
  const questions = [
    askSelect(
      'fraud_type',
      'What kind of fraud was this?',
      optionsFrom(
        FRAUD_TYPE_LABELS,
        retiredHardStop ? [...ISSUER_FILEABLE_FRAUD_TYPES, fraudType as FraudType] : ISSUER_FILEABLE_FRAUD_TYPES
      ),
      { hardStop: retiredHardStop }
    ),
  ];
  if (fraudType === 'card_not_received_as_issued') {
    questions.push(
      askDate('card_mailed_date', 'When was the card mailed to the cardholder?'),
      askText('card_mailed_postal_code', 'Postal code the card was mailed to', {
        placeholder: 'For example, 94107 or 941071234',
        maxLength: MAX_CARD_MAILED_POSTAL_CODE_LENGTH,
      })
    );
  }
  if (fraudType !== undefined && PIN_PREFERRING_FRAUD_TYPES.includes(fraudType)) {
    questions.push(
      askYesNo('pin_preferring_card', 'Is the card a PIN-preferring chip card?', {
        required: false,
        description: 'Optional. Visa assumes No when unanswered, and treats a card without a chip as No.',
      })
    );
  }
  return questions;
};

const incorrectAmountQuestions = (
  answers: ReasonAnswerMap,
  currency: string,
  ctx: DisputeRequirementContext
): ReasonQuestion[] => {
  const questions = [
    askAmount('cardholder_receipt_amount', "Amount on the cardholder's receipt", {
      description: `The correct amount in ${currency}, as opposed to the amount actually charged. The claim may not exceed the settled amount minus this value.`,
      placeholder: '0.00',
      testId: 'reason-receipt-amount',
    }),
  ];
  const receiptAmount = parseMajorUnitsToSmallestUnit(stringAnswer(answers, 'cardholder_receipt_amount'), currency);
  if (receiptAmount === undefined || receiptAmount <= 0) {
    return questions;
  }
  const label = 'Is the dispute due to a difference between the price the merchant quoted and what it charged?';
  // Mirrors `RequiresQuotedPriceDifference`: T&E merchants, receipt different from the transaction amount.
  if (!ctx.transaction) {
    questions.push(
      askYesNo('quoted_price_difference', label, {
        required: false,
        description: `Required for airline, car rental, lodging and passenger railway merchants when the receipt differs from the charge. ${TRANSACTION_UNKNOWN}`,
      })
    );
  } else if (
    isTravelAndEntertainment(ctx.transaction.merchantCategoryCode) &&
    receiptAmount !== (ctx.transaction.disputableAmount ?? 0)
  ) {
    questions.push(askYesNo('quoted_price_difference', label));
  }
  return questions;
};

/** Mirrors `CardDisputeMerchandiseServicesNotReceivedReasonDetails.MissingRequiredDetail`. */
const merchandiseServicesNotReceivedQuestions = (
  answers: ReasonAnswerMap,
  ctx: DisputeRequirementContext
): ReasonQuestion[] => {
  const expectedReceiptDate = stringAnswer(answers, 'expected_receipt_date');
  const questions = [
    merchandiseOrServicesQuestion(),
    askLongText('order_details', 'What was ordered?', { placeholder: 'Describe the order' }),
    askDate('expected_receipt_date', 'Expected receipt date'),
  ];
  if (hasDate(expectedReceiptDate) && daysBefore(expectedReceiptDate!, ctx.today) < 0) {
    questions.push(
      askLongText(
        'dispute_before_expected_date_explanation',
        'Why is the dispute being filed before the expected receipt date?'
      )
    );
  }
  questions.push(
    askYesNo('cardholder_canceled', 'Did the cardholder cancel before the expected receipt date?', {
      required: false,
      description: OPTIONAL_NO,
    })
  );
  if (answers.cardholder_canceled === true) {
    questions.push(
      askDate('cancellation_date', 'When did the cardholder cancel?'),
      askLongText('cancellation_method', 'How did the cardholder cancel?')
    );
  }
  questions.push(...resolutionQuestions(answers));

  const purchase = stringAnswer(answers, 'merchandise_or_services');
  if (purchase === CardDisputeMerchandiseOrServices.SERVICES) {
    questions.push(
      askYesNo(
        'non_fiat_currency_not_delivered',
        'Was non-fiat currency or an NFT not delivered to the wallet address the cardholder supplied at the time of the transaction?',
        { required: false, description: OPTIONAL_NO }
      ),
      askYesNo('commercial_virtual_account', 'Does the dispute involve a Visa Commercial Card Virtual Account?', {
        required: false,
        description: OPTIONAL_NO,
      })
    );
    if (answers.commercial_virtual_account === true) {
      questions.push(
        askYesNo('virtual_account_financial_loss', 'Did the virtual account holder suffer a financial loss?')
      );
    }
  } else if (purchase === CardDisputeMerchandiseOrServices.MERCHANDISE) {
    questions.push(
      askYesNo('delivered_late', 'Was the merchandise delivered late?', { required: false, description: OPTIONAL_NO }),
      askYesNo('delivered_to_wrong_location', 'Was the merchandise delivered to the wrong location?', {
        required: false,
        description: OPTIONAL_NO,
      })
    );
    if (answers.delivered_late === true || answers.delivered_to_wrong_location === true) {
      questions.push(
        askLongText(
          'late_delivery_details',
          'What was the agreed delivery location, or what are the details of the late delivery?'
        )
      );
    }
    if (answers.delivered_late === true) {
      questions.push(askYesNo('cardholder_returned_merchandise', 'Did the cardholder return the merchandise?'));
      if (answers.cardholder_returned_merchandise === true) {
        questions.push(
          askDate('returned_date', 'When did the cardholder return the merchandise?'),
          askDate('merchant_received_return_date', 'When did the merchant receive the returned merchandise?')
        );
      } else if (answers.cardholder_returned_merchandise === false) {
        questions.push(
          askYesNo('cardholder_attempted_return', 'Did the cardholder attempt to return the merchandise?')
        );
        if (answers.cardholder_attempted_return === true) {
          questions.push(
            askDate('attempted_return_date', 'When did the cardholder attempt to return the merchandise?')
          );
        }
      }
    }
  }
  if (purchase !== undefined) {
    questions.push(
      askYesNo('merchant_canceled', 'Did the merchant cancel the merchandise or services?', {
        required: false,
        description: OPTIONAL_NO,
      })
    );
    if (answers.merchant_canceled === true) {
      questions.push(askDate('merchant_cancellation_date', 'When did the merchant cancel?'));
    }
    questions.push(
      askYesNo('merchant_insolvent', 'Is the merchant insolvent or bankrupt?', {
        required: false,
        description: OPTIONAL_NO,
      }),
      askYesNo(
        'third_party_gift_card',
        'Is the purchase a third-party gift card without an expiration date, not provided because the third party is insolvent or bankrupt?',
        { required: false, description: OPTIONAL_NO }
      )
    );
  }
  return questions;
};

/** How a canceled recurring agreement ended: an explicit choice, or the one date a stored draft carries. */
export const canceledRecurringEndedBy = (
  details: DisputeReasonFormState['canceled_recurring_transaction']
): CanceledRecurringEndedBy | undefined => {
  if (details?.ended_by === 'cardholder_cancellation' || details?.ended_by === 'account_closure') {
    return details.ended_by;
  }
  const canceled = hasDate(details?.cancellation_date);
  const closed = hasDate(details?.account_closure_date);
  if (closed && !canceled) {
    return 'account_closure';
  }
  return canceled && !closed ? 'cardholder_cancellation' : undefined;
};

/** Mirrors `CardDisputeCanceledRecurringTransactionReasonDetails.MissingRequiredDetail`: exactly one of the two dates. */
const canceledRecurringQuestions = (state: DisputeReasonFormState): ReasonQuestion[] => {
  const details = state.canceled_recurring_transaction;
  const endedBy = canceledRecurringEndedBy(details);
  const bothDates = hasDate(details?.cancellation_date) && hasDate(details?.account_closure_date);
  const questions = [
    askSelect('ended_by', 'What ended the recurring agreement?', optionsFrom(CANCELED_RECURRING_ENDED_BY_LABELS), {
      serialize: false,
      derivedValue: endedBy,
      hardStop:
        endedBy === undefined && bothDates
          ? 'This draft has both a cancellation date and an account closure date, and Visa accepts only one. Choose which applies.'
          : undefined,
    }),
  ];
  if (endedBy === 'account_closure') {
    questions.push(
      askDate('account_closure_date', 'When did you inform the merchant that the account was closed?', {
        description: 'The merchant contact questions do not apply to a closed account.',
      })
    );
    return questions;
  }
  if (endedBy !== 'cardholder_cancellation') {
    return questions;
  }
  const methods = details?.merchant_contact_methods ?? [];
  const contactDetail = (field: MerchantContactDetailField, label: string, testId: string) =>
    askText(field, label, { maxLength: MERCHANT_CONTACT_DETAIL_LIMITS[field], testId });
  questions.push(
    askDate('cancellation_date', 'Cancellation date', {
      description: 'The date the recurring agreement was canceled with the merchant.',
    }),
    {
      field: 'merchant_contact_methods',
      kind: 'contact_methods',
      label: 'How did the cardholder contact the merchant to cancel?',
      required: true,
      options: optionsFrom(MERCHANT_CONTACT_METHOD_LABELS),
    }
  );
  if (methods.some((method) => method === 'call_center' || method === 'sms')) {
    questions.push(
      contactDetail('merchant_contact_phone_number', 'Phone number used to reach the merchant', 'reason-contact-phone')
    );
  }
  if (methods.includes(CardDisputeMerchantContactMethod.EMAIL)) {
    questions.push(
      contactDetail(
        'merchant_contact_email_address',
        'Email address used to reach the merchant',
        'reason-contact-email'
      )
    );
  }
  if (methods.some((method) => method === 'mail' || method === 'in_person')) {
    questions.push(
      contactDetail(
        'merchant_contact_physical_address',
        'Address where the merchant was reached',
        'reason-contact-address'
      )
    );
  }
  if (methods.includes(CardDisputeMerchantContactMethod.WEBFORM)) {
    questions.push(
      contactDetail(
        'merchant_contact_application_name',
        'App or website used to reach the merchant',
        'reason-contact-application'
      )
    );
  }
  questions.push(
    askText('cancellation_method', 'How was the agreement canceled?', {
      placeholder: 'For example, an email to support or an account-settings change',
    })
  );
  return questions;
};

/** Mirrors `CardDisputeNotAsDescribedReasonDetails.MissingRequiredDetail`. */
const notAsDescribedQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const purchase = stringAnswer(answers, 'merchandise_or_services');
  const questions = [merchandiseOrServicesQuestion()];
  if (purchase === CardDisputeMerchandiseOrServices.SERVICES) {
    questions.push(nonFiatNotAsDescribedQuestion());
  }
  questions.push(
    askDate('received_date', 'Date the merchandise or services were received'),
    ...negotiationQuestions(answers, ctx),
    askLongText('order_details', 'What was ordered, and how did it differ from what was received?', {
      placeholder: 'Describe the order and the difference',
    }),
    ...resolutionQuestions(answers)
  );
  if (purchase === CardDisputeMerchandiseOrServices.SERVICES) {
    questions.push(...servicesCancellationQuestions(answers, ctx), ...foodQualityQuestions(ctx));
  } else if (purchase === CardDisputeMerchandiseOrServices.MERCHANDISE) {
    questions.push(
      ...returnTreeQuestions(answers, {
        trackingNumberMethods: CARRIER_RETURN_METHODS,
        refusalReasons: ALL_REFUSAL_REASONS,
      })
    );
  }
  return questions;
};

const counterfeitQuestions = (): ReasonQuestion[] => [
  askLongText('order_details', 'What was ordered?', { placeholder: 'Describe the order' }),
  askDate('counterfeit_discovered_date', 'Date the counterfeit was discovered'),
  askLongText('counterfeit_details', 'How was the merchandise determined to be counterfeit?'),
  askLongText('merchandise_disposition', 'Current disposition of the merchandise', {
    placeholder: 'For example, retained by the cardholder',
  }),
];

/** Mirrors `CardDisputeMisrepresentationReasonDetails.MissingRequiredDetail`. */
const misrepresentationQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const purchase = stringAnswer(answers, 'merchandise_or_services');
  const questions = [
    merchandiseOrServicesQuestion(),
    askDate('received_date', 'When did the cardholder receive the merchandise or services?'),
    ...negotiationQuestions(answers, ctx),
    askLongText('order_details', 'What did the cardholder purchase?'),
    askLongText('misrepresentation_details', 'How was the merchandise or service misrepresented?'),
  ];
  if (purchase === CardDisputeMerchandiseOrServices.SERVICES) {
    // VROL asks a services claim about resolving it only once the cardholder canceled.
    questions.push(askYesNo('cardholder_canceled', 'Did the cardholder cancel the services?'));
    if (answers.cardholder_canceled === true) {
      questions.push(
        askDate('cancellation_date', 'When did the cardholder cancel the services?'),
        askLongText('cancellation_method', 'How and why did the cardholder cancel the services?'),
        ...resolutionQuestions(answers)
      );
    }
  } else if (purchase === CardDisputeMerchandiseOrServices.MERCHANDISE) {
    questions.push(
      ...resolutionQuestions(answers),
      ...returnTreeQuestions(answers, {
        trackingNumberMethods: CARRIER_OR_OTHER_RETURN_METHODS,
        refusalReasons: MERCHANT_DIRECTED_REFUSAL_REASONS,
        misrepresentation: true,
      })
    );
  }
  return questions;
};

/** Mirrors `CardDisputeCreditNotProcessedReasonDetails.MissingRequiredDetail`. */
const creditNotProcessedQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const dated = answers.credit_voucher_dated !== false;
  const questions = [
    askYesNo('credit_voucher_dated', 'Is the credit voucher, transaction receipt or refund acknowledgement dated?', {
      required: false,
      description: 'Optional. Treated as Yes when unanswered.',
    }),
    dated
      ? askDate('credit_voucher_date', 'Credit voucher date', {
          description:
            'The date the merchant issued the credit voucher or transaction receipt that was never processed.',
        })
      : askDate('canceled_or_returned_date', 'When did the cardholder cancel the service or return the merchandise?'),
  ];
  const creditDate = stringAnswer(answers, dated ? 'credit_voucher_date' : 'canceled_or_returned_date');
  if (!hasDate(creditDate)) {
    return questions;
  }
  const label =
    'Why was the credit issued late, and what are the details of the negotiations between the cardholder and the merchant?';
  // Mirrors `RequiresLateCreditExplanation`: the credit dated more than 113 days after the transaction was processed.
  if (!ctx.transaction) {
    questions.push(
      askLongText('late_credit_explanation', label, {
        required: false,
        description: `Required when the credit is dated more than 113 days after the transaction. ${TRANSACTION_UNKNOWN}`,
      })
    );
  } else if (
    ctx.transaction.processingDate !== undefined &&
    daysBefore(ctx.transaction.processingDate, creditDate!) > CREDIT_ISSUED_LATE_DAYS
  ) {
    questions.push(
      askLongText('late_credit_explanation', label, {
        description: 'Visa asks this once the credit is dated more than 120 days after the transaction.',
      })
    );
  }
  return questions;
};

/** The other-transaction tree shared by duplicate processing and paid by other means. Mirrors `CardDisputeOtherTransactionDetails.missingRequiredDetail`. */
const otherTransactionQuestions = (
  answers: ReasonAnswerMap,
  ctx: DisputeRequirementContext,
  otherTransactionLabel: string
): ReasonQuestion[] => {
  // Visa compares card numbers, and so does the backend: a renewed or damaged-card replacement that keeps the card
  // number is the same card, while a lost-card replacement with a new number is a different card.
  const questions = [
    askYesNo('other_transaction_on_same_card', 'Are both transactions for the same merchant and on the same card?', {
      description: 'A renewed or damaged-card replacement that keeps the card number counts as the same card.',
    }),
  ];
  const sameCard = answers.other_transaction_on_same_card;
  if (sameCard === false) {
    questions.push(
      askYesNo(
        'other_transaction_on_different_card',
        'Is the other transaction for the same merchant and on a different Visa card of the same cardholder?',
        { description: 'A different card number, such as a replacement for a lost or stolen card.' }
      )
    );
  }
  const differentCard = answers.other_transaction_on_different_card;
  if (sameCard === true || (sameCard === false && differentCard === true)) {
    questions.push(
      askText('other_card_transaction_id', otherTransactionLabel, {
        placeholder: 'ctxn_...',
        description:
          sameCard === true
            ? 'A card transaction on the same card number as the disputed transaction, including on a renewed card.'
            : 'A card transaction on a different card number of the same cardholder.',
        maxLength: undefined,
        hardStop: cardTransactionIdHardStop(stringAnswer(answers, 'other_card_transaction_id'), ctx),
      })
    );
    return questions;
  }
  if (sameCard !== false || differentCard !== false) {
    return questions;
  }
  questions.push(
    askYesNo('other_transaction_paid_by_other_means', 'Was the other transaction paid by other means?', {
      hardStop:
        answers.other_transaction_paid_by_other_means === false
          ? 'Visa does not accept this dispute unless the other transaction was paid by other means.'
          : undefined,
    })
  );
  if (answers.other_transaction_paid_by_other_means !== true) {
    return questions;
  }
  const proof = stringAnswer(answers, 'other_payment_proof');
  questions.push(
    askSelect(
      'other_payment_proof',
      'What proof of the payment by other means is available?',
      optionsFrom(OTHER_PAYMENT_PROOF_LABELS),
      {
        description:
          proof !== undefined && DOCUMENT_PAYMENT_PROOFS.includes(proof)
            ? 'Attach the proof as a document before submitting.'
            : undefined,
      }
    )
  );
  if (proof === CardDisputeOtherPaymentProof.ACQUIRER_REFERENCE_NUMBER) {
    const value = trimmed(stringAnswer(answers, 'other_acquirer_reference_number'));
    questions.push(
      askText('other_acquirer_reference_number', "What is the other transaction's acquirer reference number?", {
        maxLength: undefined,
        hardStop:
          value !== undefined && !/^(\d{23}|\d{24})$/.test(value)
            ? 'Enter the 23- or 24-digit acquirer reference number.'
            : undefined,
      })
    );
  } else if (proof === CardDisputeOtherPaymentProof.NETWORK_TRANSACTION_ID) {
    const value = trimmed(stringAnswer(answers, 'other_network_transaction_id'));
    questions.push(
      askText('other_network_transaction_id', "What is the other transaction's Visa transaction identifier?", {
        maxLength: undefined,
        hardStop:
          value !== undefined && !/^(\d{15}|\d{17})$/.test(value)
            ? 'Enter the 15-digit Visa transaction identifier, or 17 digits with the multiple clearing sequence number.'
            : undefined,
      })
    );
  } else if (proof === CardDisputeOtherPaymentProof.THIRD_PARTY_VOUCHER) {
    questions.push(
      askYesNo(
        'merchant_passed_on_funds_evidence',
        'Do you have evidence that the first merchant passed the funds on to the second merchant?',
        {
          hardStop:
            answers.merchant_passed_on_funds_evidence === false
              ? 'Visa does not accept this dispute unless you have evidence the first merchant passed the funds on.'
              : undefined,
        }
      )
    );
  }
  return questions;
};

/**
 * Mirrors `CardDisputeIncorrectCurrencyReasonDetails.MissingRequiredDetail` and the write-time checks scoped to each
 * currency error: the correct currency must differ from the one the merchant processed the transaction in, and the
 * dynamic currency conversion certification only files as Yes.
 */
const incorrectCurrencyQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const currencyError = stringAnswer(answers, 'currency_error');
  const questions = [
    askSelect('currency_error', 'What was wrong with the transaction currency?', optionsFrom(CURRENCY_ERROR_LABELS)),
  ];
  if (currencyError === CardDisputeCurrencyError.CURRENCY_DIFFERENCE) {
    const transactionCurrency = ctx.transaction?.transactionCurrency;
    questions.push(
      askSelect('correct_currency', 'What currency should the transaction have been processed in?', CURRENCY_OPTIONS, {
        description: transactionCurrency
          ? `The merchant processed the transaction in ${transactionCurrency}; the correct currency must differ.`
          : 'Must differ from the currency the merchant processed the transaction in.',
        hardStop:
          transactionCurrency !== undefined && stringAnswer(answers, 'correct_currency') === transactionCurrency
            ? `The merchant already processed the transaction in ${transactionCurrency}. Choose the currency it should have been processed in instead.`
            : undefined,
      })
    );
  } else if (currencyError === CardDisputeCurrencyError.DYNAMIC_CURRENCY_CONVERSION) {
    questions.push(
      askYesNo(
        'cardholder_did_not_agree_to_dcc',
        'Do you certify that the cardholder did not agree to dynamic currency conversion and did not make an active choice?',
        {
          hardStop:
            answers.cardholder_did_not_agree_to_dcc === false
              ? 'Visa does not accept this dispute: No certifies the cardholder agreed to the conversion the dispute claims they did not.'
              : undefined,
        }
      )
    );
  }
  return questions;
};

/** Mirrors `CardDisputeIncorrectTransactionCodeReasonDetails.MissingRequiredDetail`. */
const incorrectTransactionCodeQuestions = (
  answers: ReasonAnswerMap,
  ctx: DisputeRequirementContext
): ReasonQuestion[] => {
  const codeError = stringAnswer(answers, 'transaction_code_error');
  const questions = [
    askSelect(
      'transaction_code_error',
      'How was the transaction code incorrect?',
      optionsFrom(TRANSACTION_CODE_ERROR_LABELS),
      {
        hardStop:
          codeError === CardDisputeTransactionCodeError.CREDIT_POSTED_AS_DEBIT ||
          codeError === CardDisputeTransactionCodeError.DEBIT_POSTED_AS_CREDIT
            ? 'Column cannot file this: Visa requires the dispute to claim twice the transaction amount, more than a dispute can claim.'
            : undefined,
      }
    ),
  ];
  if (codeError === CardDisputeTransactionCodeError.CREDIT_INSTEAD_OF_REVERSAL) {
    questions.push(
      askText(
        'credit_card_transaction_id',
        'Which card transaction is the credit the merchant processed instead of a reversal or adjustment?',
        {
          placeholder: 'ctxn_...',
          description: 'A credit on the same card account as the disputed transaction.',
          maxLength: undefined,
          hardStop: cardTransactionIdHardStop(stringAnswer(answers, 'credit_card_transaction_id'), ctx),
        }
      ),
      askLongText('credit_error_explanation', 'Why was the credit refund processed in error?')
    );
  }
  return questions;
};

/** Mirrors `CardDisputeCanceledMerchandiseOrServicesReasonDetails.MissingRequiredDetail`. */
const canceledMerchandiseOrServicesQuestions = (
  answers: ReasonAnswerMap,
  ctx: DisputeRequirementContext
): ReasonQuestion[] => {
  const purchase = stringAnswer(answers, 'merchandise_or_services');
  const services = purchase === CardDisputeMerchandiseOrServices.SERVICES;
  const merchandise = purchase === CardDisputeMerchandiseOrServices.MERCHANDISE;
  const serviceType = stringAnswer(answers, 'service_type');
  const questions = [merchandiseOrServicesQuestion('Was the canceled purchase merchandise or services?')];
  if (services) {
    questions.push(askSelect('service_type', 'What type of service was canceled?', optionsFrom(SERVICE_TYPE_LABELS)));
  }
  if (merchandise || (services && serviceType === CardDisputeServiceType.OTHER)) {
    questions.push(askLongText('order_details', 'What did the cardholder purchase?'));
  }
  if (merchandise) {
    questions.push(
      askYesNo('cardholder_received_merchandise', 'Did the cardholder receive the merchandise?'),
      askDate(
        'received_or_expected_date',
        'When did the cardholder receive the merchandise, or when did they expect to receive it?'
      )
    );
  }
  questions.push(...resolutionQuestions(answers));

  if (services && serviceType !== undefined) {
    questions.push(...canceledServicesQuestions(answers, ctx, serviceType));
  } else if (merchandise && answers.cardholder_received_merchandise !== undefined) {
    questions.push(...canceledMerchandiseQuestions(answers, ctx, answers.cardholder_received_merchandise === true));
  }
  return questions;
};

/** Mirrors `missingMerchandiseDetail` and `missingMerchandiseCancellationDetail` for canceled merchandise. */
const canceledMerchandiseQuestions = (
  answers: ReasonAnswerMap,
  ctx: DisputeRequirementContext,
  received: boolean
): ReasonQuestion[] => {
  const questions: ReasonQuestion[] = [];
  if (received) {
    questions.push(
      ...returnTreeQuestions(answers, {
        trackingNumberMethods: CARRIER_OR_OTHER_RETURN_METHODS,
        refusalReasons: MERCHANT_DIRECTED_REFUSAL_REASONS,
      })
    );
  }
  questions.push(
    askYesNo(
      'cardholder_canceled',
      'Did the cardholder cancel?',
      received ? { required: false, description: OPTIONAL_NO } : {}
    )
  );
  const canceled = answers.cardholder_canceled === true;
  if (received && canceled) {
    questions.push(askYesNo('canceled_before_shipping', 'Did the cardholder cancel before the merchandise shipped?'));
  }
  const canceledBeforeShipping = received && canceled && answers.canceled_before_shipping === true;
  if (canceledBeforeShipping || (!received && canceled)) {
    questions.push(
      askDate('cancellation_date', 'When did the cardholder cancel?'),
      askLongText('cancellation_method', 'How and why did the cardholder cancel?')
    );
  }
  if (!received || canceledBeforeShipping) {
    questions.push(askYesNo('cancellation_policy_provided', 'Was a cancellation policy provided?'));
  }
  if (canceledBeforeShipping) {
    questions.push(...merchantAcceptedCancellationQuestions(answers, ctx));
  }
  return questions;
};

/** Mirrors `missingServicesDetail` and `missingServicesCancellationDetail` for canceled services. */
const canceledServicesQuestions = (
  answers: ReasonAnswerMap,
  ctx: DisputeRequirementContext,
  serviceType: string
): ReasonQuestion[] => {
  const guaranteedReservation = serviceType === CardDisputeServiceType.GUARANTEED_RESERVATION;
  const questions: ReasonQuestion[] = [];
  if (!guaranteedReservation) {
    questions.push(
      askYesNo('cardholder_canceled', 'Did the cardholder cancel?', {
        hardStop:
          serviceType === CardDisputeServiceType.OTHER && answers.cardholder_canceled === false
            ? 'Visa only accepts other services as canceled when the cardholder canceled them.'
            : undefined,
      })
    );
  }
  if (serviceType === CardDisputeServiceType.TIMESHARE) {
    questions.push(
      askDate(
        'timeshare_date',
        'What is the date of the timeshare, or the date the contract or related documents were received?'
      )
    );
  } else {
    questions.push(askDate('service_date', 'When was the service provided, or when was it expected to be?'));
  }
  const certification = stringAnswer(answers, 'guaranteed_reservation_certification');
  if (guaranteedReservation) {
    questions.push(
      askSelect(
        'guaranteed_reservation_certification',
        'What does the cardholder certify about the guaranteed reservation?',
        optionsFrom(RESERVATION_CERTIFICATION_LABELS)
      )
    );
  }
  const canceled = !guaranteedReservation && answers.cardholder_canceled === true;
  const certifiesCancellation =
    guaranteedReservation &&
    (certification === CardDisputeReservationCertification.PROPERLY_CANCELED ||
      certification === CardDisputeReservationCertification.ATTEMPTED_CANCELLATION_WITHIN_24_HOURS);
  if (canceled || certifiesCancellation) {
    questions.push(askDate('cancellation_date', 'When did the cardholder cancel?'));
  }
  if (canceled) {
    questions.push(askLongText('cancellation_method', 'How and why did the cardholder cancel?'));
  }
  const timeshareDate = stringAnswer(answers, 'timeshare_date');
  const cancellationDate = stringAnswer(answers, 'cancellation_date');
  const lateTimeshareCancellation =
    serviceType === CardDisputeServiceType.TIMESHARE &&
    canceled &&
    hasDate(timeshareDate) &&
    hasDate(cancellationDate) &&
    daysBefore(timeshareDate!, cancellationDate!) > TIMESHARE_CANCELLATION_DAYS;
  if (guaranteedReservation || lateTimeshareCancellation) {
    questions.push(askYesNo('cancellation_policy_provided', 'Was a cancellation policy provided?'));
  }
  if (canceled) {
    questions.push(...merchantAcceptedCancellationQuestions(answers, ctx));
  }
  return questions;
};

/** Mirrors `CardDisputeDamagedOrDefectiveMerchandiseReasonDetails.MissingRequiredDetail`. */
const damagedOrDefectiveQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => [
  askLongText('order_details', 'What did the cardholder order, and how was it damaged or defective?'),
  askDate('received_date', 'When did the cardholder receive the merchandise?'),
  ...negotiationQuestions(answers, ctx),
  ...resolutionQuestions(answers),
  ...returnTreeQuestions(answers, {
    trackingNumberMethods: CARRIER_RETURN_METHODS,
    refusalReasons: ALL_REFUSAL_REASONS,
  }),
];

/** Mirrors `CardDisputeQualityOfMerchandiseOrServicesReasonDetails.MissingRequiredDetail`. */
const qualityQuestions = (answers: ReasonAnswerMap, ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const purchase = stringAnswer(answers, 'merchandise_or_services');
  const questions = [merchandiseOrServicesQuestion()];
  if (purchase === CardDisputeMerchandiseOrServices.SERVICES) {
    questions.push(nonFiatNotAsDescribedQuestion());
  }
  questions.push(
    askLongText('order_details', 'What did the cardholder purchase, and what is the quality issue?'),
    askDate('received_date', 'When did the cardholder receive the merchandise or services?'),
    ...negotiationQuestions(answers, ctx),
    ...resolutionQuestions(answers)
  );
  if (purchase === CardDisputeMerchandiseOrServices.SERVICES) {
    questions.push(
      askYesNo('cardholder_paid_for_work_redone', 'Did the cardholder pay to have the work redone?'),
      ...servicesCancellationQuestions(answers, ctx),
      ...foodQualityQuestions(ctx)
    );
  } else if (purchase === CardDisputeMerchandiseOrServices.MERCHANDISE) {
    questions.push(
      ...returnTreeQuestions(answers, {
        trackingNumberMethods: CARRIER_OR_OTHER_RETURN_METHODS,
        refusalReasons: ALL_REFUSAL_REASONS,
      })
    );
  }
  return questions;
};

/** Mirrors `CardDisputeTermsOfContractReasonDetails.MissingRequiredDetail`. */
const termsOfContractQuestions = (ctx: DisputeRequirementContext): ReasonQuestion[] => {
  const processingDate = ctx.transaction?.processingDate;
  const required =
    ctx.transaction !== undefined &&
    processingDate !== undefined &&
    daysBefore(processingDate, ctx.today) > LATE_CLAIM_DAYS;
  const description = !ctx.transaction
    ? `Required once the transaction is more than 113 days old. ${TRANSACTION_UNKNOWN}`
    : required
      ? undefined
      : 'Optional until the transaction is more than 113 days old.';
  return [
    askLongText(
      'terms_not_honored_details',
      'How were the terms of the contract not honored by the merchant, or not as described?',
      { description: 'Terms-of-contract disputes apply to Visa Commercial Card Virtual Account transactions.' }
    ),
    askDate('received_date', 'When did the cardholder receive the services?', { required, description }),
  ];
};

/**
 * The questions the current answers ask for a reason, in the backend's `MissingRequiredDetail`
 * order, so the first missing answer names the same field the API would. Over-long free text is a
 * hard stop on its question.
 */
export const reasonQuestions = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext = disputeRequirementContext()
): ReasonQuestion[] => withLengthHardStops(askedQuestions(reason, state, currency, ctx), answersOf(state, reason));

const askedQuestions = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext
): ReasonQuestion[] => {
  const answers = answersOf(state, reason);
  switch (reason) {
    case 'fraud':
      return fraudQuestions(answers);
    case 'no_authorization':
      return [
        askSelect(
          'account_status',
          'Why should the authorization have been declined?',
          optionsFrom(ACCOUNT_STATUS_LABELS, Object.values(CardDisputeAccountStatus))
        ),
      ];
    case 'incorrect_amount':
      return incorrectAmountQuestions(answers, currency, ctx);
    case 'incorrect_account_number':
      return [
        askYesNo(
          'receipt_account_number_matches',
          "Does the account number on the cardholder's receipt match the cardholder's card number or token?"
        ),
      ];
    case 'merchandise_services_not_received':
      return merchandiseServicesNotReceivedQuestions(answers, ctx);
    case 'canceled_recurring_transaction':
      return canceledRecurringQuestions(state);
    case 'not_as_described':
      return notAsDescribedQuestions(answers, ctx);
    case 'counterfeit_merchandise':
      return counterfeitQuestions();
    case 'misrepresentation':
      return misrepresentationQuestions(answers, ctx);
    case 'credit_not_processed':
      return creditNotProcessedQuestions(answers, ctx);
    case 'duplicate_processing':
      return otherTransactionQuestions(answers, ctx, 'Which card transaction is the other charge?');
    case 'paid_by_other_means':
      return [
        ...otherTransactionQuestions(answers, ctx, 'Which card transaction is the other payment?'),
        ...resolutionQuestions(answers),
      ];
    case 'incorrect_currency':
      return incorrectCurrencyQuestions(answers, ctx);
    case 'incorrect_transaction_code':
      return incorrectTransactionCodeQuestions(answers, ctx);
    case 'invalid_data':
      return [
        askLongText(
          'authorization_decline_explanation',
          'Why would including valid data have caused the authorization request to be declined?'
        ),
      ];
    case 'canceled_merchandise_or_services':
      return canceledMerchandiseOrServicesQuestions(answers, ctx);
    case 'damaged_or_defective_merchandise':
      return damagedOrDefectiveQuestions(answers, ctx);
    case 'quality_of_merchandise_or_services':
      return qualityQuestions(answers, ctx);
    case 'original_credit_not_accepted':
      return [
        askSelect(
          'not_accepted_reason',
          'Why was the original credit not accepted?',
          optionsFrom(ORIGINAL_CREDIT_NOT_ACCEPTED_REASON_LABELS)
        ),
        askLongText('explanation', 'What are the details of why the original credit was not accepted?'),
      ];
    case 'terms_of_contract':
      return termsOfContractQuestions(ctx);
    default:
      return [];
  }
};

/** The answer to a question: its derived value, or the answer stored under its field. */
export const questionAnswer = (
  question: ReasonQuestion,
  state: DisputeReasonFormState,
  reason: CardDisputeReason
): ReasonAnswer => question.derivedValue ?? answersOf(state, reason)[question.field];

/** Whether an answer is supplied and valid for its question. Unknown select values count as unanswered. */
const isAnswered = (question: ReasonQuestion, value: ReasonAnswer, currency: string): boolean => {
  switch (question.kind) {
    case 'boolean':
      return typeof value === 'boolean';
    case 'date':
      return typeof value === 'string' && hasDate(value);
    case 'text':
    case 'multiline':
      return typeof value === 'string' && trimmed(value) !== undefined;
    case 'select':
      return typeof value === 'string' && (question.options ?? []).some((option) => option.value === value);
    case 'amount': {
      const amount = typeof value === 'string' ? parseMajorUnitsToSmallestUnit(value, currency) : undefined;
      return amount !== undefined && amount > 0;
    }
    case 'contact_methods':
      return (
        Array.isArray(value) && value.length > 0 && value.every((method) => CONTACT_METHOD_VALUES.includes(method))
      );
    default:
      return false;
  }
};

const serializedAnswer = (question: ReasonQuestion, value: ReasonAnswer, currency: string): unknown => {
  if (!isAnswered(question, value, currency)) {
    return undefined;
  }
  switch (question.kind) {
    case 'amount':
      return parseMajorUnitsToSmallestUnit(value as string, currency);
    case 'text':
    case 'multiline':
    case 'date':
      return (value as string).trim();
    case 'contact_methods':
      return [...(value as string[])];
    default:
      return value;
  }
};

/**
 * Required questionnaire answers that are missing for the selected reason, plus answers Visa
 * hard-stops, which the backend also reports as missing. Unknown enum values are treated as missing
 * so the form never reports readiness on a value the backend would reject.
 */
export const missingReasonFields = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext = disputeRequirementContext()
): ReasonFieldKey[] =>
  reasonQuestions(reason, state, currency, ctx)
    .filter(
      (question) =>
        question.hardStop !== undefined ||
        (question.required && !isAnswered(question, questionAnswer(question, state, reason), currency))
    )
    .map((question) => question.field);

export interface ReasonHardStop {
  field: ReasonFieldKey;
  message: string;
}

/** Answers the API rejects on write or Visa hard-stops. Any of them blocks saving the questionnaire. */
export const findReasonHardStops = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext = disputeRequirementContext()
): ReasonHardStop[] =>
  reasonQuestions(reason, state, currency, ctx).flatMap((question) =>
    question.hardStop !== undefined ? [{ field: question.field, message: question.hardStop }] : []
  );

/**
 * Why Visa hard-stops the dispute amount for the reason's answers and the transaction amount, both in the dispute
 * currency's smallest unit, or undefined. Mirrors `cardDisputeAmountHardStops`: a partial-only reason claiming the whole
 * amount (`amount_not_partial`), and an incorrect-amount claim above the charge less the receipt
 * (`amount_exceeds_receipt_difference`). Nothing is checked while the transaction amount is unknown.
 */
export const disputeAmountHardStop = (input: {
  reason: CardDisputeReason;
  claimAmount: number | undefined;
  transactionAmount: number | undefined;
  formState: DisputeReasonFormState;
  currency: string;
}): string | undefined => {
  const { reason, claimAmount, transactionAmount } = input;
  if (claimAmount === undefined || transactionAmount === undefined || transactionAmount <= 0) {
    return undefined;
  }
  if (reasonRequiresPartialClaim(reason) && claimAmount >= transactionAmount) {
    return reason === 'incorrect_amount'
      ? 'An incorrect-amount claim must be less than the transaction amount: it disputes the difference from the receipt.'
      : 'An incorrect-transaction-code claim must be less than the transaction amount.';
  }
  if (reason !== 'incorrect_amount') {
    return undefined;
  }
  const receiptAmount = parseMajorUnitsToSmallestUnit(
    input.formState.incorrect_amount?.cardholder_receipt_amount,
    input.currency
  );
  return exceedsReceiptDifference({ claimAmount, settledAmount: transactionAmount, receiptAmount })
    ? 'For incorrect-amount disputes the claim may not exceed the transaction amount minus the receipt amount.'
    : undefined;
};

/**
 * Serialize only the selected reason's sub-object, and in it only the answers the current
 * questionnaire asks. When the reason or a branching answer changes, no other reason's detail
 * object and no stale hidden answer is ever sent.
 */
export const buildReasonDetailsPayload = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext = disputeRequirementContext()
): CardDisputeReasonDetails | undefined => {
  const payload: Record<string, unknown> = {};
  reasonQuestions(reason, state, currency, ctx).forEach((question) => {
    if (question.serialize === false) {
      return;
    }
    const value = serializedAnswer(question, questionAnswer(question, state, reason), currency);
    if (value !== undefined) {
      payload[question.field] = value;
    }
  });
  return Object.keys(payload).length > 0 ? ({ [reason]: payload } as CardDisputeReasonDetails) : undefined;
};

/** Per-reason form state reconstructed from a dispute's stored reason details, for draft repair. */
export const reasonFormStateFromDetails = (
  reason: CardDisputeReason,
  details: CardDisputeReasonDetails | undefined,
  currency: string
): DisputeReasonFormState => {
  if (REASON_WITHOUT_DETAILS.includes(reason)) {
    return emptyReasonFormState();
  }
  const stored = ((details as Partial<Record<string, Record<string, unknown>>> | undefined)?.[reason] ?? {}) as Record<
    string,
    unknown
  >;
  const answers: Record<string, ReasonAnswer> = {};
  Object.entries(stored).forEach(([field, value]) => {
    if (typeof value === 'boolean' || typeof value === 'string') {
      answers[field] = value;
    } else if (typeof value === 'number') {
      // The receipt amount is the only numeric answer: smallest units back to the input string.
      answers[field] = formatSmallestUnitToMajorUnits(value, currency);
    } else if (Array.isArray(value)) {
      answers[field] = value.map(String);
    }
  });
  if (reason === 'canceled_recurring_transaction') {
    answers.merchant_contact_methods = answers.merchant_contact_methods ?? [];
  }
  return { [reason]: answers } as DisputeReasonFormState;
};

/** Whether the network rejects the dispute without an uploaded document. Mirrors `RequiresEvidenceDocumentForReason`. */
export const requiresEvidenceDocument = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext = disputeRequirementContext()
): boolean => {
  if (REASON_REQUIRES_DOCUMENT[reason]) {
    return true;
  }
  if (reason !== 'duplicate_processing' && reason !== 'paid_by_other_means') {
    return false;
  }
  // The proof is only in play while the questionnaire asks for it.
  const asked = reasonQuestions(reason, state, currency, ctx).some(
    (question) => question.field === 'other_payment_proof'
  );
  const proof = stringAnswer(answersOf(state, reason), 'other_payment_proof');
  if (!asked || proof === undefined) {
    return false;
  }
  return (
    DOCUMENT_PAYMENT_PROOFS.includes(proof) ||
    (proof === CardDisputeOtherPaymentProof.THIRD_PARTY_VOUCHER &&
      answersOf(state, reason).merchant_passed_on_funds_evidence === true)
  );
};

const PAYMENT_PROOF_DOCUMENT_GUIDANCE: Record<string, string> = {
  [CardDisputeOtherPaymentProof.CASH_RECEIPT]:
    'Attach the cash receipt. Visa requires a document for this proof of payment.',
  [CardDisputeOtherPaymentProof.CANCELLED_CHECK]:
    'Attach the cancelled check. Visa requires a document for this proof of payment.',
  [CardDisputeOtherPaymentProof.OTHER_CARD_STATEMENT]:
    'Attach the statement from the other card. Visa requires a document for this proof of payment.',
  [CardDisputeOtherPaymentProof.OTHER]:
    'Attach the proof of payment. Visa requires a document for this proof of payment.',
  [CardDisputeOtherPaymentProof.THIRD_PARTY_VOUCHER]:
    'Attach the evidence that the first merchant passed the funds on to the second merchant. Visa requires a document.',
};

/**
 * Plain-language evidence guidance for the reason and answers: the documents the network requires,
 * and the requirements the dashboard cannot evaluate, such as the repeat-claim cardholder letter,
 * which depends on the card's other disputes.
 */
export const evidenceGuidance = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext = disputeRequirementContext()
): string[] => {
  const guidance: string[] = [];
  switch (reason) {
    case 'incorrect_amount':
      guidance.push("Attach the cardholder's receipt showing the correct amount. Visa requires a document.");
      break;
    case 'credit_not_processed':
      guidance.push(
        'Attach the credit voucher, transaction receipt or refund acknowledgement. Visa requires a document.'
      );
      break;
    case 'counterfeit_merchandise':
      guidance.push('Attach proof that the merchandise is counterfeit. Visa requires a document.');
      break;
    case 'duplicate_processing':
    case 'paid_by_other_means': {
      if (!requiresEvidenceDocument(reason, state, currency, ctx)) {
        break;
      }
      const proof = stringAnswer(answersOf(state, reason), 'other_payment_proof');
      guidance.push(PAYMENT_PROOF_DOCUMENT_GUIDANCE[proof ?? ''] ?? PAYMENT_PROOF_DOCUMENT_GUIDANCE.other);
      break;
    }
    case 'merchandise_services_not_received':
    case 'non_receipt_cash_at_atm':
      guidance.push(
        'If this card already has two submitted disputes with this reason against the same merchant within 30 days, attach a cardholder letter (evidence category Cardholder letter). Visa requires one from the third claim on.'
      );
      break;
    default:
      break;
  }
  return guidance;
};

/**
 * Submission readiness for a dispute being edited (new or resumed draft). `missing` carries
 * human-readable gaps so the UI can explain what a submit would fail on without calling it.
 */
export interface DisputeSubmissionReadiness {
  ready: boolean;
  missing: string[];
}

export const disputeSubmissionReadiness = (input: {
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  currency: string;
  context?: DisputeRequirementContext;
  /** Narrative typed in the current session; already-attached narrative evidence counts separately. */
  narrative?: string;
  /** Narrative evidence already attached to the dispute (resumed drafts). */
  hasAttachedNarrativeEvidence?: boolean;
  /** Document evidence selected in the current session. */
  evidence: CardDisputeDocumentEvidenceInput[];
  /** Document evidence already attached to the dispute (resumed drafts). */
  attachedDocumentCount?: number;
  /** Whether a partial-claim explanation is required: claim below the original settled amount. */
  requiresPartialExplanation?: boolean;
  partialExplanation?: string;
}): DisputeSubmissionReadiness => {
  const missing: string[] = [];
  const context = input.context ?? disputeRequirementContext();

  if (missingReasonFields(input.reason, input.formState, input.currency, context).length > 0) {
    missing.push('required questionnaire answers');
  }
  if (
    REASON_REQUIRES_NARRATIVE[input.reason] &&
    !input.hasAttachedNarrativeEvidence &&
    trimmed(input.narrative) === undefined
  ) {
    missing.push('a written statement describing the problem');
  }
  if (
    requiresEvidenceDocument(input.reason, input.formState, input.currency, context) &&
    (input.attachedDocumentCount ?? 0) + (input.evidence ?? []).length === 0
  ) {
    missing.push('at least one supporting document');
  }
  if (
    input.requiresPartialExplanation &&
    reasonRequiresPartialExplanation(input.reason) &&
    trimmed(input.partialExplanation) === undefined
  ) {
    missing.push('an explanation of why the claim is less than the full transaction amount');
  }

  return { ready: missing.length === 0, missing };
};

/** The explanation limit is Unicode characters, not bytes, per the backend. */
export const reasonForPartialDisputeTooLong = (value: string | undefined): boolean =>
  value !== undefined && [...value].length > MAX_REASON_FOR_PARTIAL_DISPUTE_LENGTH;

export interface DisputeFormPayload {
  reason: CardDisputeReason;
  reason_details?: CardDisputeReasonDetails;
  cardholder_narrative?: string;
  evidence?: CardDisputeDocumentEvidenceInput[];
  reason_for_partial_dispute?: string;
}

/**
 * Build the shared request payload for eligibility checks and creation. Narrative and evidence are
 * included only when supplied; creation adds amount/transaction/currency and the credit flag.
 */
export const buildDisputeFormPayload = (input: {
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  currency: string;
  context?: DisputeRequirementContext;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
  partialExplanation?: string;
}): DisputeFormPayload => {
  const payload: DisputeFormPayload = { reason: input.reason };
  const reasonDetails = buildReasonDetailsPayload(input.reason, input.formState, input.currency, input.context);
  if (reasonDetails) {
    payload.reason_details = reasonDetails;
  }
  const narrative = trimmed(input.narrative);
  if (narrative !== undefined) {
    payload.cardholder_narrative = narrative;
  }
  const evidence = input.evidence ?? [];
  if (evidence.length > 0) {
    payload.evidence = evidence;
  }
  const partialExplanation = trimmed(input.partialExplanation);
  if (partialExplanation !== undefined) {
    payload.reason_for_partial_dispute = partialExplanation;
  }
  return payload;
};

/** The eligibility request for the current form state. A passing eligibility call alone does not prove the submit request is complete (it does not carry the partial explanation). */
export const buildEligibilityRequest = (input: {
  cardTransactionId: string;
  amount: number;
  currency: string;
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  context?: DisputeRequirementContext;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
}): CardDisputeEligibilityRequest => {
  const { reason, reason_details, cardholder_narrative, evidence } = buildDisputeFormPayload({
    reason: input.reason,
    formState: input.formState,
    currency: input.currency,
    context: input.context,
    narrative: input.narrative,
    evidence: input.evidence,
  });

  const request: CardDisputeEligibilityRequest = {
    card_transaction_id: input.cardTransactionId,
    amount: input.amount,
    currency: input.currency,
    reason: toApiReason(reason),
  };
  if (reason_details) request.reason_details = reason_details;
  if (cardholder_narrative) request.cardholder_narrative = cardholder_narrative;
  if (evidence) request.evidence = evidence;
  return request;
};

/**
 * The create request for the wizard's draft-first flow. Always `submit: false`: network submission
 * is a separate deliberate action, never combined with a draft save. The partial explanation is
 * only carried for partial claims of reasons that use it, and the credit flag is sent exactly as
 * confirmed (never for a reason that does not support it).
 */
export const buildCreateDisputeRequest = (input: {
  cardTransactionId: string;
  claimAmount: number;
  currency: string;
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  context?: DisputeRequirementContext;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
  partial: boolean;
  partialExplanation?: string;
  issueProvisionalCredit: boolean;
}): CreateCardDisputeRequest => {
  const payload = buildDisputeFormPayload({
    reason: input.reason,
    formState: input.formState,
    currency: input.currency,
    context: input.context,
    narrative: input.narrative,
    evidence: input.evidence,
    partialExplanation:
      input.partial && reasonRequiresPartialExplanation(input.reason) ? input.partialExplanation : undefined,
  });

  const request: CreateCardDisputeRequest = {
    card_transaction_id: input.cardTransactionId,
    amount: input.claimAmount,
    currency: input.currency,
    reason: toApiReason(payload.reason),
    issue_provisional_credit: input.issueProvisionalCredit && reasonSupportsProvisionalCredit(input.reason),
    submit: false,
  };
  if (payload.reason_details) request.reason_details = payload.reason_details;
  if (payload.cardholder_narrative) request.cardholder_narrative = payload.cardholder_narrative;
  if (payload.evidence) request.evidence = payload.evidence;
  if (payload.reason_for_partial_dispute) request.reason_for_partial_dispute = payload.reason_for_partial_dispute;
  return request;
};

/**
 * The ordered `/evidence` calls that persist draft corrections: reason details first, then the
 * partial explanation, then one call per new narrative/document. Strings are trimmed; blank
 * changes are dropped. Empty-string updates are never sent — the backend merge cannot clear a
 * stored string, so corrections replace rather than erase.
 */
export const buildDraftRepairCalls = (input: {
  reason: CardDisputeReason;
  currency: string;
  context?: DisputeRequirementContext;
  storedDetails: CardDisputeReasonDetails | undefined;
  formState: DisputeReasonFormState;
  storedPartialExplanation: string | undefined;
  partialExplanationVisible: boolean;
  nextPartialExplanation: string;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
}): AddCardDisputeEvidenceRequest[] => {
  const calls: AddCardDisputeEvidenceRequest[] = [];

  const storedPayload = buildReasonDetailsPayload(
    input.reason,
    reasonFormStateFromDetails(input.reason, input.storedDetails, input.currency),
    input.currency,
    input.context
  );
  const nextDetails = buildReasonDetailsPayload(input.reason, input.formState, input.currency, input.context);
  if (nextDetails && JSON.stringify(storedPayload) !== JSON.stringify(nextDetails)) {
    calls.push({ reason_details: nextDetails });
  }

  const storedPartial = (input.storedPartialExplanation ?? '').trim();
  const nextPartial = input.nextPartialExplanation.trim();
  if (input.partialExplanationVisible && nextPartial !== '' && storedPartial !== nextPartial) {
    calls.push({ reason_for_partial_dispute: nextPartial });
  }

  const narrative = trimmed(input.narrative);
  if (narrative !== undefined) {
    calls.push({ narrative });
  }

  (input.evidence ?? []).forEach((document) => {
    calls.push({ document_id: document.document_id, evidence_type: document.evidence_type });
  });

  return calls;
};

/** Whether the current claim is partial: below the original settled amount for the transaction. */
export const isPartialClaim = (claimAmount: number, originalSettledAmount: number): boolean =>
  claimAmount < originalSettledAmount;

/** Whether an incorrect-amount claim exceeds the settled amount minus the cardholder receipt amount. */
export const exceedsReceiptDifference = (input: {
  claimAmount: number;
  settledAmount: number;
  receiptAmount: number | undefined;
}): boolean => {
  if (input.receiptAmount === undefined) {
    return false;
  }
  return input.claimAmount > input.settledAmount - input.receiptAmount;
};

const formatAnswer = (question: ReasonQuestion, value: ReasonAnswer, currency: string): string => {
  switch (question.kind) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'select':
      return question.options?.find((option) => option.value === value)?.label ?? String(value);
    case 'amount':
      return `${(value as string).trim()} ${currency}`;
    case 'contact_methods':
      return (value as string[])
        .map((method) => question.options?.find((option) => option.value === method)?.label ?? method)
        .join(', ');
    default:
      return (value as string).trim();
  }
};

/**
 * Human-readable questionnaire answers for the review step: the answers to the questions the
 * questionnaire currently asks, labeled. Only supplied answers appear.
 */
export const questionnaireAnswers = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  ctx: DisputeRequirementContext = disputeRequirementContext()
): { field: ReasonFieldKey; label: string; answer: string }[] =>
  reasonQuestions(reason, state, currency, ctx).flatMap((question) => {
    const value = questionAnswer(question, state, reason);
    return isAnswered(question, value, currency)
      ? [{ field: question.field, label: question.label, answer: formatAnswer(question, value, currency) }]
      : [];
  });

/** Extract the dispute's reason as a typed CardDisputeReason, or undefined for unknown values. */
export const disputeReasonOf = (dispute: CardDispute): CardDisputeReason | undefined => {
  const reason = dispute.reason as string;
  return Object.prototype.hasOwnProperty.call(REASON_REQUIRES_NARRATIVE, reason)
    ? (reason as CardDisputeReason)
    : undefined;
};
